/** @format */

import { useDispatch, useSelector } from "react-redux";

import {
	Box,
	CircularProgress,
	Grid,
	Stack,
	styled,
	Typography,
} from "@mui/material";
import { colors, gridSpacing } from "../../utility/types/constant";
import {
	AddMemberBtn,
	AnnouncementBtn,
	CircleDetails,
	CircleMemberTable,
	ContributionCycle,
	CurrentContributorsCard,
	LoadingSpinner,
	// ExpectedContributionAmountCard,
	// ExpectedTotalContributionLightCard,
	// ExpectedTotalPayOutLightCard,
	MemberChatBtn,
	MoreCircleDetails,
	PayoutOrderBtn,
	PayoutOrderModal,
	SearchUserToSendAddRequestModal,
	SendInviteBtn,
	TotalCircleContributionCard,
	TotalContributionGrowthBarChart,
	TotalCurrentContributionCard,
	TotalCurrentPayOutDarkCard,
	UpdateCircle,
	UpdateCircleSettingsBtn,
	ViewAndSearchAddRequestSendOut,
	ViewMembersBtn,
	ViewSendAddRequestBtn,
} from "../allComponents/AllComponents";
import { useEffect, useState } from "react";
import Main from "../../utility/common/Main";
// import { Item } from "../banner-one/styles";
import { AppDispatch } from "../../redux/store/store";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import {
	selectCircleData,
	// selectCircleExpectedTotalPayOut,
	selectUnionCycleBalances,
	// selectIsLoading,
} from "../../redux/selectors/circleSelector";
import {
	fetchCircleById,
	getCircleExpectedTotalPayOut,
	getUnionOrCircleCycleBalances,
} from "../../redux/actions/circleAction";
import { clearUnionCycleBalances } from "../../redux/reducers/circleReducer";
import { useParams } from "react-router-dom";
import {
	// selectCurrentContributionCycleById,
	selectCurrentCycleOfCircle,
} from "../../redux/selectors/contributionSelector";
import {
	getContributionCycleById,
	getCurrentCycleOfCircle,
} from "../../redux/actions/contributionAction";
import MainCard from "../cards/MainCard";
import UserProfileHeader from "../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../drawerNavBar/DrawerNavbar";
import SendCircleInviteModal from "../modal/circle-invitation-modal/SendCircleInviteModal";

const CardWrapper = styled(MainCard)(({ theme }) => ({
	overflow: "hidden",
	position: "relative",
	"&:after": {
		content: '""',
		position: "absolute",
		width: 210,
		height: 210,
		background: `linear-gradient(210.04deg, ${colors.mediumGold} -50.94%, rgba(144, 202, 249, 0) 83.49%)`,
		borderRadius: "50%",
		top: -30,
		right: -180,
	},
	"&:before": {
		content: '""',
		position: "absolute",
		width: 210,
		height: 210,
		background: `linear-gradient(140.9deg, ${colors.mediumGold} -14.02%, rgba(144, 202, 249, 0) 70.50%)`,
		borderRadius: "50%",
		top: -160,
		right: -130,
	},
}));

const CircleViewDashboard = () => {
	const [openNav, setOpenNav] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	const user = useSelector(selectCurrentUser);
	const { circleId } = useParams();

	const circleData = useSelector(selectCircleData);
	// Use circleId from URL params for the selector (more reliable)
	const currentCycle = useSelector((state: any) =>
		selectCurrentCycleOfCircle(state, circleId ?? ""),
	);

	const unionCycleBalances = useSelector(selectUnionCycleBalances);

	// Track individual loading states
	const [isLoadingCircle, setIsLoadingCircle] = useState(true);
	const [isLoadingCycle, setIsLoadingCycle] = useState(true);
	const [isLoadingBalances, setIsLoadingBalances] = useState(false);

	// Combined loading state - show spinner until essential data is ready
	const isLoading = isLoadingCircle || isLoadingCycle;
	const showLoading = isLoading || isLoadingBalances;

	const [showSearchUserToSendAddRequest, setShowSearchUserToSendAddRequest] =
		useState(false);

	const [showSendInviteModal, setShowSendInviteModal] = useState(false);

	const [showViewAndSearchAddRequest, setShowViewAndSearchAddRequest] =
		useState(false);

	const [openUpdateCircleModal, setOpenUpdateCircleModal] = useState(false);

	const [openMemberModal, setOpenMemberModal] = useState(false);

	const [openCircleDetails, setOpenCircleDetails] = useState(false);
	const [openPayoutOrder, setOpenPayoutOrder] = useState(false);

	// Fetch all data in sequence with proper chaining
	useEffect(() => {
		let isMounted = true;

		const fetchAllData = async () => {
			if (!user?._id || !circleId) {
				setIsLoadingCircle(false);
				setIsLoadingCycle(false);
				return;
			}

			// Clear stale balance data from previous circle before fetching new data
			dispatch(clearUnionCycleBalances());

			try {
				// Step 1: Fetch circle data
				setIsLoadingCircle(true);
				setIsLoadingCycle(true);

				await dispatch(fetchCircleById(circleId)).unwrap();

				if (!isMounted) return;
				setIsLoadingCircle(false);

				// Step 2: Fetch current cycle
				try {
					const cycleResult = await dispatch(
						getCurrentCycleOfCircle({ unionId: circleId }),
					).unwrap();

					if (!isMounted) return;
					setIsLoadingCycle(false);

					// Step 3: If we have a cycle, fetch balances
					if (cycleResult?._id) {
						setIsLoadingBalances(true);
						try {
							await Promise.all([
								dispatch(
									getContributionCycleById({ cycleId: cycleResult._id }),
								).unwrap(),
								dispatch(
									getUnionOrCircleCycleBalances({
										unionId: circleId,
										cycleId: cycleResult._id,
									}),
								).unwrap(),
								dispatch(
									getCircleExpectedTotalPayOut({ unionId: circleId }),
								).unwrap(),
							]);
						} catch (balanceError: any) {
							console.error("Failed to load circle cycle data:", balanceError);
						} finally {
							if (isMounted) {
								setIsLoadingBalances(false);
							}
						}
					}
				} catch (cycleError: any) {
					// No active cycle is OK

					if (isMounted) {
						setIsLoadingCycle(false);
					}
				}
			} catch (error: any) {
				if (isMounted) {
					setIsLoadingCircle(false);
					setIsLoadingCycle(false);
				}
			}
		};

		fetchAllData();

		return () => {
			isMounted = false;
		};
	}, [dispatch, user?._id, circleId]);

	const selectedCircle = circleData;
	const circleLeaderId = selectedCircle?.leader?._id ?? selectedCircle?.leader;

	const cycleContributions = unionCycleBalances?.cycleContributions;

	// Ledger-based data when available, fallback to the cycle's running total
	const totalContribution =
		cycleContributions?.totalAmount != null ?
			parseFloat(cycleContributions.totalAmount)
			: (currentCycle?.contributionsTotal ?? 0);

	const formattedTotalContribution = Number(totalContribution).toFixed(2);

	// Net of Stripe fees when ledger data is available
	const currentPayOut =
		cycleContributions?.netAmount != null ?
			parseFloat(cycleContributions.netAmount)
			: totalContribution;

	const formattedCurrentPayOut = Number(currentPayOut).toFixed(2);

	const circleBalance =
		cycleContributions?.netAmount ?? formattedTotalContribution;

	// // Show loading spinner while essential data is being fetched
	// if (isLoading) {
	// 	return (
	// 		<Main>
	// 			<Box
	// 				component="div"
	// 				sx={{
	// 					display: "flex",
	// 					justifyContent: "center",
	// 					alignItems: "center",
	// 					minHeight: "60vh",
	// 					flexDirection: "column",
	// 					gap: 2,
	// 				}}>
	// 				<LoadingSpinner
	// 					loading={isLoading}
	// 					size={45}
	// 					sx={{ position: "absolute" }}
	// 				/>
	// 				{/* <Typography variant="body1" color="text.secondary">
	// 					Loading circle data...
	// 				</Typography> */}
	// 			</Box>
	// 		</Main>
	// 	);
	// }

	if (!circleData && !showLoading) {
		return (
			<Box
				component="div"
				sx={{
					justifyContent: "center",
					display: "flex",
					textAlign: "center",
				}}>
				<Typography variant="h4" color="text.secondary" sx={{ mt: 10 }}>
					Something unexpected happened.Circle Data Not Found. Please refresh
					page.
				</Typography>
			</Box>
		);
	}

	const handleOpenSearchUserToSendAddRequest = () => {
		setShowSearchUserToSendAddRequest(true);
	};

	const handleCloseSearchUserToSendAddRequest = () => {
		setShowSearchUserToSendAddRequest(false);
	};

	const handleShowSendInvite = () => {
		setShowSendInviteModal(true);
	};

	const handleCloseShowSendInvite = () => {
		setShowSendInviteModal(false);
	};

	const handleOpen = () => {
		setOpenUpdateCircleModal(true);
	};

	const handleClose = () => {
		setOpenUpdateCircleModal(false);
	};

	const handleOpenMemberModal = () => {
		setOpenMemberModal(true);
	};

	const handleCloseMemberModal = () => {
		setOpenMemberModal(false);
	};
	const handleOpenViewAndSearchAddRequest = () => {
		setShowViewAndSearchAddRequest(true);
	};

	const handleCloseViewAndSearchAddRequest = () => {
		setShowViewAndSearchAddRequest(false);
	};

	const handleOpenCircleDetails = () => {
		setOpenCircleDetails(true);
	};

	const handleCloseCircleDetails = () => {
		setOpenCircleDetails(false);
	};

	const handleOpenPayoutOrder = () => {
		setOpenPayoutOrder(true);
	};

	const handleClosePayoutOrder = () => {
		setOpenPayoutOrder(false);
	};

	return (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />

			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				{isLoading ?
					<LoadingSpinner
						loading={isLoading}
						size={45}
						sx={{ position: "absolute" }}
					/>
					: <DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				}
				<Main>
					<Grid container spacing={gridSpacing}>
						<Grid item xs={12}>
							<Grid container spacing={gridSpacing}>
								<Grid item lg={4} md={6} sm={6} xs={12}>
									<TotalCircleContributionCard
										isLoading={isLoading}
										circleConnectedAccountBalance={circleBalance || "0"}
									/>
								</Grid>
								<Grid item lg={4} md={6} sm={6} xs={12}>
									<TotalCurrentContributionCard
										isLoading={isLoading}
										totalCurrentCycleContributionAmount={
											formattedTotalContribution
										}
									/>
								</Grid>
								<Grid item lg={4} md={12} sm={12} xs={12}>
									<Grid container spacing={gridSpacing}>
										<Grid item sm={6} xs={12} md={6} lg={12}>
											<TotalCurrentPayOutDarkCard
												isLoading={isLoading}
												currentPayOut={formattedCurrentPayOut}
											/>
										</Grid>
										<Grid item sm={6} xs={12} md={6} lg={12}>
											<CardWrapper border={false} content={false}>
												<Box sx={{ display: "flex", gap: 1 }}>
													<ContributionCycle />
													<CircleDetails onClick={handleOpenCircleDetails} />
												</Box>
											</CardWrapper>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Stack
								direction="row"
								spacing={1.5}
								flexWrap="wrap"
								useFlexGap
								sx={{
									alignItems: "stretch",
									justifyContent: "flex-start",
									"& > *": {
										flexBasis: { xs: "calc(33.333% - 8px)", sm: "auto" },
										flexShrink: 0,
										flexGrow: { xs: 1, sm: 0 },
									},
								}}
							>
								<ViewMembersBtn onClick={handleOpenMemberModal} />

								{user?._id === circleLeaderId && (
									<>
										<AddMemberBtn onClick={handleOpenSearchUserToSendAddRequest} />
										<UpdateCircleSettingsBtn onClick={handleOpen} />
										<ViewSendAddRequestBtn onClick={handleOpenViewAndSearchAddRequest} />
									</>
								)}

								{user?.isVerified && (
									<SendInviteBtn onClick={handleShowSendInvite} />
								)}

								<PayoutOrderBtn onClick={handleOpenPayoutOrder} />
							</Stack>
						</Grid>
						<Grid item xs={12}>
							<Grid container spacing={gridSpacing}>
								<Grid item xs={12} md={8}>
									{currentCycle?._id ?
										<TotalContributionGrowthBarChart
											isLoading={isLoading}
											circleName={circleData?.circleName ?? ""}
											currentCycleTotalContribution={formattedTotalContribution}
											unionId={circleData?._id ?? ""}
											cycleId={currentCycle._id}
											enableRealTime={true}
										/>
										: <MainCard>
											<Typography
												variant="h6"
												textAlign="center"
												color="text.secondary"
												sx={{ py: 4 }}>
												No active contribution cycle. Start a cycle to see
												contribution data.
											</Typography>
										</MainCard>
									}
								</Grid>
								<Grid item xs={12} md={4}>
									<CurrentContributorsCard isLoading={isLoading} />
								</Grid>
							</Grid>
						</Grid>

					</Grid>

					{showSearchUserToSendAddRequest && (
						<SearchUserToSendAddRequestModal
							open={showSearchUserToSendAddRequest}
							handleClose={handleCloseSearchUserToSendAddRequest}
						/>
					)}

					{openUpdateCircleModal && (
						<UpdateCircle
							open={openUpdateCircleModal}
							handleClose={handleClose}
							circleId={circleId ?? ""}
						/>
					)}

					{showViewAndSearchAddRequest && (
						<ViewAndSearchAddRequestSendOut
							open={showViewAndSearchAddRequest}
							handleClose={handleCloseViewAndSearchAddRequest}
							circleId={circleId ?? ""}
						/>
					)}

					{openMemberModal && (
						<CircleMemberTable
							open={openMemberModal}
							handleClose={handleCloseMemberModal}
						/>
					)}

					{openCircleDetails && (
						<MoreCircleDetails
							open={openCircleDetails}
							handleClose={handleCloseCircleDetails}
						/>
					)}

					{showSendInviteModal && (
						<SendCircleInviteModal
							open={showSendInviteModal}
							handleClose={handleCloseShowSendInvite}
							circleId={circleId ?? ""}
							circleName={circleData?.circleName ?? ""}
						/>
					)}

					<PayoutOrderModal
						open={openPayoutOrder}
						handleClose={handleClosePayoutOrder}
						circleId={circleId ?? ""}
					/>
				</Main>
			</Box>
		</>
	);
};

export default CircleViewDashboard;
